import type { FC } from "react";
import type { CarReservationPaymentProps } from "@apac/cars";
import { useModuleBEM } from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import { getVehicleClassLabelFromVehicle } from "@tenant/cars";
import clsx from "clsx";
import defaultStyles from "./CarReservationCheckoutBreakdown.module.scss";
import { useCarReservationCheckoutBreakdown } from "@/modules/cars-postbooking/hooks";

export interface CarReservationCheckoutBreakdownProps
  extends CarReservationPaymentProps {
  className?: string;
}

const ComponentName = "CarReservationCheckoutBreakdown";

export const CarReservationCheckoutBreakdown: FC<
  CarReservationCheckoutBreakdownProps
> = ({ itinerary, className }) => {
  const { t, formatFiatCurrency, formatRewardsCurrency } = useI18nContext();
  const [block, cn] = useModuleBEM(defaultStyles, ComponentName);
  const {
    reservation: { vehicle },
  } = itinerary;

  const {
    baseRate,
    total,
    fees,
    payAtDesk,
    amountPaid,
    pointsRedeemed,
    pointsRedeemedFiat,
    travelCreditApplied,
    hasPayAtDesk,
    paymentMethodDisplay,
  } = useCarReservationCheckoutBreakdown(itinerary);

  return (
    <div className={clsx(block, className)}>
      <h3 className={cn("title")}>{t("trips.checkoutBreakdown")}</h3>

      <section className={cn("section")}>
        <div className={cn("section-title")}>
          {getVehicleClassLabelFromVehicle(t, vehicle)}
        </div>

        <div className={cn("section-detail")}>
          <div className={cn("section-label")}>
            {t("core-cars.priceBreakdown.baseFare")}
          </div>
          <div className={cn("section-value")}>
            {formatFiatCurrency(baseRate)}
          </div>
        </div>

        <div className={cn("section-detail")}>
          <div className={cn("section-label")}>
            {t("core-cars.priceBreakdown.taxesAndFees")}
          </div>
          <div className={cn("section-value")}>{formatFiatCurrency(fees)}</div>
        </div>
      </section>

      <section className={cn("section", ["total"])}>
        <div className={cn("section-detail")}>
          <div className={cn("section-label")}>
            {t("core-cars.priceBreakdown.total")}
          </div>
          <div className={cn("section-value")}>{total.priceText}</div>
        </div>
        {payAtDesk &&
          typeof payAtDesk.value === "number" &&
          payAtDesk.value !== 0 && (
            <div className={cn("section-detail")}>
              <div className={cn("section-label")}>
                {t("core-cars.priceBreakdown.payAtDesk")}
              </div>
              <div className={cn("section-value")}>
                {formatFiatCurrency(payAtDesk)}
              </div>
            </div>
          )}
      </section>

      {travelCreditApplied ? (
        <section className={cn("section")}>
          <div className={cn("section-detail")}>
            <div className={cn("section-label")}>
              {t("trips.travelCreditsApplied")}
            </div>
            <div className={cn("section-value", ["redeemed"])}>
              {travelCreditApplied}
            </div>
          </div>
        </section>
      ) : null}

      {pointsRedeemed ? (
        <section className={cn("section")}>
          <div className={cn("section-detail")}>
            <div className={cn("section-label")}>
              {t("trips.redeemedPoints", {
                points: formatRewardsCurrency(
                  {
                    value: pointsRedeemed.value.numberOfPoints,
                    currency: "", // defaults to "pts"
                  },
                  { minimumFractionDigits: 0, maximumFractionDigits: 0 }
                ),
              })}
            </div>
            <div className={cn("section-value", ["redeemed"])}>
              {`-${pointsRedeemedFiat}`}
            </div>
          </div>
        </section>
      ) : null}

      <div className={cn("section")}>
        <div className={cn("section-detail", ["paid"])}>
          <div className={cn("section-label")}>{t("trips.amountPaid")}</div>
          <div className={cn("section-value")}>
            {formatFiatCurrency(amountPaid)}
          </div>
        </div>
        {paymentMethodDisplay && (
          <div className={cn("section-detail")}>
            <div className={cn("section-label")}>
              {t("trips.paymentMethod")}
            </div>
            <div className={cn("section-value")}>{paymentMethodDisplay}</div>
          </div>
        )}
      </div>

      {hasPayAtDesk ? (
        <section className={cn("section", ["pay-later"])}>
          <div className={cn("section-detail")}>
            <div className={cn("section-label")}>
              {t("core-cars.priceBreakdown.payAtDesk")}
            </div>
            <div className={cn("section-value")}>
              {formatFiatCurrency(payAtDesk)}
            </div>
          </div>
        </section>
      ) : null}
    </div>
  );
};
